import { FormattedUnitLabels, FormatterInput } from '@/helpers/unit-formatter/shared/types';
import {
  secondsToDuration,
  toNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/shared/utils';

/**
 * Formatter for duration values. Accepts raw seconds (number) or pre-formatted H:MM strings.
 * Negative seconds pass through to `secondsToDuration` as-is (e.g. `-1:00`); compact
 * formatting via `formatCompactDuration` rejects negatives instead.
 */
export const formatDuration = ({ value }: FormatterInput): FormattedUnitLabels => {
  let displayValue: FormatterInput['value'] = value;

  if (typeof value === 'string' && value.includes(':')) {
    displayValue = value;
  } else {
    const numeric = toNumber(value);
    if (numeric != null) {
      displayValue = secondsToDuration(numeric);
    }
  }

  return unitValueToFormattedUnitLabels({ value: displayValue, units: undefined });
};

/**
 * Format a duration in seconds as a compact `3d 2h 14m` / `1h 23m` / `12m 5s`
 * / `45s` style string.
 */
export const formatCompactDuration = (totalSeconds: number): string => {
  // Negative durations are domain-invalid; clamp to 0 rather than display a misleading compact label.
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};
